import { Reducer, useEffect, useReducer, useState } from 'react';
import camelCase from 'lodash/camelCase';
import isEqual from 'lodash/isEqual';
import find from 'lodash/find';

import { splitArray, dedupeListValuesByProperty } from 'utils/arrayHelpers';

import { DEFAULT_CATEGORY_ID, SELECTED_OPTIONS_CATEGORY_ID } from '../constants';
import {
  buildUseCategoryState,
  convertMapToArray,
  createSelectedOption,
  getDefaultState,
  getFilterFunction,
  getKeysInMap,
  getNodeText,
  matchCategoryAndFilter,
  matchFilter,
  sortByIndex,
  UseCategorySelectionState,
} from './useCategorySelection.utils';
import {
  CategoryDetail,
  SelectionState,
  BaseOption,
  OnSelectHandler,
  SelectValueType,
} from '../types';

const ROOT_ACTION = 'category-selection';

const ACTIONS = {
  CLEAR_ALL: `${ROOT_ACTION}/clear-all`,
  GROUP_BY_SELECTED: `${ROOT_ACTION}/group-by-selected`,
  SELECT_NONE: `${ROOT_ACTION}/select-none`,
  SELECT_ALL: `${ROOT_ACTION}/select-all`,
  SINGLE_SELECT: `${ROOT_ACTION}/single-select`,
  SET_SELECT_MATCH_OPTIONAL: `${ROOT_ACTION}/set-select-match-optional`,
  TOGGLE: `${ROOT_ACTION}/toggle`,
  UPDATE_OPTIONS: `${ROOT_ACTION}/update-options`,
  UPDATE_SELECTIONS: `${ROOT_ACTION}/update-selections`,
};

/* istanbul ignore next */
const logWarningIfNeeded = ({
  type,
  isExternalSearchUsed,
}: {
  type: string;
  isExternalSearchUsed?: boolean;
}) => {
  if (!isExternalSearchUsed || process.env.NODE_ENV === 'production') return;
  const fnName = camelCase(type.split('/')[1]);

  console.warn(`
    \`${fnName}\` has not been configured to work when \`isExternalSearchUsed\` is true.
    Proceed with caution!
  `);
};

function createReducer<TValue extends SelectValueType = string>(): Reducer<
  UseCategorySelectionState<TValue>,
  any
> {
  return (state: UseCategorySelectionState<TValue> = getDefaultState(), action) => {
    switch (action.type) {
      case ACTIONS.CLEAR_ALL: {
        state.selectedOptions.clear();

        return {
          ...state,
          options: state.options.map((option) => ({ ...option, isSelected: false })),
        };
      }

      case ACTIONS.GROUP_BY_SELECTED: {
        const sorted = state.options.sort(
          (a, b) =>
            Number(b.isSelected) - Number(a.isSelected) ||
            getNodeText(a.label).localeCompare(getNodeText(b.label), undefined, {
              numeric: true,
              sensitivity: 'base',
            }),
        );
        return {
          ...state,
          options: action.groupSelectedInCategory
            ? sorted.map((opt) => ({
                ...opt,
                data: {
                  ...opt.data,
                  isInSelectedCategory: opt.isSelected,
                },
              }))
            : sorted,
        };
      }

      case ACTIONS.SELECT_ALL: {
        logWarningIfNeeded(action);

        const filterFn = getFilterFunction(action);
        const [match, unmatched] = splitArray(state.options, filterFn);
        const updatedOptions = match.map((o) => {
          const option = { ...o };
          // Disabled options should be ignored
          if (!o.isDisabled) {
            option.isSelected = true;
            state.selectedOptions.set(o.value, option);
          }
          return option;
        });

        return { ...state, options: [...unmatched, ...updatedOptions] };
      }

      case ACTIONS.SELECT_NONE: {
        logWarningIfNeeded(action);
        const filterFn = getFilterFunction(action);
        const [match, unmatched] = splitArray(state.options, filterFn);

        const updatedOptions = match.map((o) => {
          const option = { ...o };
          // Disabled options should be ignored
          if (!o.isDisabled) {
            option.isSelected = false;
            state.selectedOptions.delete(o.value);
          }

          return option;
        });

        return { ...state, options: [...unmatched, ...updatedOptions] };
      }

      case ACTIONS.SINGLE_SELECT: {
        const [match, unmatched] = splitArray(
          state.options,
          (option) => option.categoryId === action.category,
        );

        state.selectedOptions.clear();
        const selectedOptions = match.map((o) => {
          const isSelected = o.value === action.value;
          const option = {
            ...o,
            isSelected,
          };

          if (isSelected) {
            state.selectedOptions.set(o.value, option);
          }

          return option;
        });

        return {
          ...state,
          options: [...selectedOptions, ...unmatched.map((o) => ({ ...o, isSelected: false }))],
        };
      }

      case ACTIONS.SET_SELECT_MATCH_OPTIONAL: {
        const { value } = action;

        state.selectedOptions.clear();

        const selectedOptions = new Map();

        // scenario: User inputs "Email" thru input, not by selecting from the dropdown
        // options contains { value: "emailaddress", label: "Email"}
        // desired result: matchedOption = { value: "emailaddress", label: "Email"}
        const matchedOption = state.options.find(
          (option) => option.label === value || option.value === value,
        );
        if (matchedOption) {
          selectedOptions.set(value, { ...matchedOption, isSelected: true });
        } else {
          // must be a custom value
          selectedOptions.set(value, createSelectedOption(value));
        }

        const newOptions = state.options.map((opt) =>
          opt.value === matchedOption?.value
            ? { ...opt, isSelected: true }
            : { ...opt, isSelected: false },
        );
        return { ...state, selectedOptions, options: newOptions };
      }

      case ACTIONS.TOGGLE: {
        const [match, unmatched] = splitArray(
          state.options,
          (option) => option.categoryId === action.category,
        );

        const updatedOptions = match.map((o) => {
          const isOptionToggled = o.value === action.value;
          const option = { ...o, isSelected: isOptionToggled ? !o.isSelected : o.isSelected };

          if (isOptionToggled) {
            if (option.isSelected) {
              state.selectedOptions.set(o.value, option);
            } else {
              state.selectedOptions.delete(o.value);
            }
          }

          return option;
        });

        return { ...state, options: [...updatedOptions, ...unmatched] };
      }

      case ACTIONS.UPDATE_OPTIONS: {
        return action.value;
      }

      case ACTIONS.UPDATE_SELECTIONS: {
        const { selectedOptions = new Map(), options = [] } = state;
        const { selectedValues = [] } = action;
        // No actual change, so return the current state
        if (isEqual(getKeysInMap(selectedOptions).sort(), selectedValues.sort())) {
          return state;
        }

        const updatedSelectedOptions = new Map();

        (selectedValues as TValue[]).forEach((v) => {
          const option =
            selectedOptions.get(v) || // look in current selected items...
            find(options, { value: v }) || // look in options list...
            find(options, { label: v }) || // look in options list but with matching label...
            createSelectedOption(v); // can't find in either, so create a stub
          updatedSelectedOptions.set(v, { ...option, isSelected: true });
        });

        const updatedOptions = options.map((o) => ({
          ...o,
          isSelected: selectedValues.includes(o.value),
        }));

        return { ...state, selectedOptions: updatedSelectedOptions, options: updatedOptions };
      }

      default: {
        return state;
      }
    }
  };
}

/**
  Returns [state, details, actions]
  state: the reducer state
  details: introspection shortcuts on the state
  actions: fns to fire to change the state
 */

type Props<TValue extends SelectValueType = string> = {
  callback: OnSelectHandler<TValue>;
  defaultSelections: TValue[];
  filterText?: string;
  options: BaseOption<TValue>[];
  isExternalSearchUsed?: boolean;
  showAllCategories?: boolean;
  hasSelectAllOption?: boolean;
  groupBySelectedOptions?: boolean;
  groupSelectedInCategory?: boolean;
};

/**
   The data structure returned by `useCategorySelection` is ornate
   {
     optionsByCategory: a map of all the options by their category ID
     optionsList: a list of all the options
     selectAllInCategory: fn - the function to flip all items to checked
     selectNoneInCategory: fn - the function to flip all items to unchecked
     selectedOptions: string[] - the selectedOptions ids
     showHeaders: boolean - if there are more than 1 category
     singleSelect: fn- the selection fn for a basic select
     toggle: fn - flip a single option back and forth
   }

   Note: listening for the state to change in this function is either hard or
   I wasn't doing it correctly. But it seems pretty trivial to put the useEffect
   hook inside the useCategorySelection hook. So when the hook that manages internal
   state change detects a change, it will reliably trigger the `onSelect` fn
   */

export function useCategorySelection<TValue extends SelectValueType = string>({
  callback = () => undefined,
  defaultSelections = [],
  filterText = '',
  options = [],
  isExternalSearchUsed = false,
  showAllCategories = false,
  groupBySelectedOptions = false,
  groupSelectedInCategory = false,
}: Props<TValue>): SelectionState<TValue> {
  const [didMount, setDidMount] = useState(false);

  // Instantiating useReducer this way makes sure that it doesn't rebuild on
  // ever render (which ends up causing all sorts of performance issues)
  // See "Lazy Initialization" in the link below:
  // LINK: https://reactjs.org/docs/hooks-reference.html#usereducer
  const [state, dispatch] = useReducer(createReducer<TValue>(), getDefaultState<TValue>(), () =>
    buildUseCategoryState<TValue>(defaultSelections, options),
  );

  /** Value used for options array comparison in the useEffect hook below */
  const optionComparisonValue = options
    .map(
      (opt) =>
        `${opt.value}.${opt.label}.${JSON.stringify(opt.data)}.${opt.isDisabled && 'disabled'}`,
    )
    .join('-');
  const defaultSelectionsComparisonValue = defaultSelections.join('-');
  /** Compares category ids in order to update option state with locales */
  const categoryIdValue = options.map((opt) => opt.categoryId).join('-');

  /**
    HTML selects are controlled by a `value` so we're exposing that on every selection change.
    NOTE: both single and multi-selects will return an array of options for consistency in the
    underlying implementation and minimize type mismatching
   */
  useEffect(() => {
    if (didMount) {
      callback(getKeysInMap(state?.selectedOptions), {
        selectedOptions: convertMapToArray(state?.selectedOptions),
      });
    }
    setDidMount(true); // we don't want this to run on the initial mount, only on change
  }, [state?.options]);

  const clearAll = () => dispatch({ type: ACTIONS.CLEAR_ALL });
  const selectAllInCategory = (category: string, filter?: string) =>
    dispatch({
      type: ACTIONS.SELECT_ALL,
      category,
      filter,
      isExternalSearchUsed,
    });
  const selectNoneInCategory = (category: string, filter?: string) =>
    dispatch({
      type: ACTIONS.SELECT_NONE,
      category,
      filter,
      isExternalSearchUsed,
    });
  const selectAll = (filter?: string) => {
    dispatch({
      type: ACTIONS.SELECT_ALL,
      category: DEFAULT_CATEGORY_ID,
      filter,
      isExternalSearchUsed,
    });
  };
  const selectNone = (filter?: string) => {
    dispatch({
      type: ACTIONS.SELECT_NONE,
      category: DEFAULT_CATEGORY_ID,
      filter,
      isExternalSearchUsed,
    });
  };
  const singleSelect = (category: string, value: TValue) =>
    dispatch({ category, type: ACTIONS.SINGLE_SELECT, value });
  const setSelectWithoutMatch = (category: string, value: TValue) =>
    dispatch({ category, type: ACTIONS.SET_SELECT_MATCH_OPTIONAL, value });
  const toggle = (category: string, value: TValue) => {
    dispatch({ category, type: ACTIONS.TOGGLE, value });
  };
  const updateOptions = (value: UseCategorySelectionState<TValue>) =>
    dispatch({ type: ACTIONS.UPDATE_OPTIONS, value });
  const updateSelections = () =>
    dispatch({ type: ACTIONS.UPDATE_SELECTIONS, selectedValues: defaultSelections });
  const groupAndSortBySelectedOptions = () =>
    dispatch({ type: ACTIONS.GROUP_BY_SELECTED, groupSelectedInCategory });

  /** Update state options in reducer if options passed in has changed */
  useEffect(() => {
    updateOptions(buildUseCategoryState(defaultSelections, options, state));

    /** Group selected options at the top of the list */
    if (groupSelectedInCategory) {
      groupAndSortBySelectedOptions();
    }
    setDidMount(false);
  }, [optionComparisonValue, categoryIdValue, groupSelectedInCategory]);

  useEffect(() => {
    updateSelections();
  }, [defaultSelectionsComparisonValue]);

  /**
    Get a deduped list of all the possible categories
    returns ['category1', 'category2', etc...]
   */
  const categoryList = [
    ...(groupSelectedInCategory ? [SELECTED_OPTIONS_CATEGORY_ID] : []),
    ...dedupeListValuesByProperty(options, 'categoryId'),
  ];

  const categoryDetail = categoryList.reduce(
    (acc: Record<string, CategoryDetail>, category) => {
      if (!acc[category]) {
        const matchedOptions = state?.options.filter((option) =>
          matchCategoryAndFilter(category, filterText, option),
        );
        acc[category] = {
          // hasSelectAllOption does not guarantee a select all on the backend when external search is used. If the external search returns paginated data, the select all will only select the data that is sent to frontend.
          areAllOptionsSelected: matchedOptions.every((option) => option.isSelected),
          categoryId: category,
        };
      }
      return acc;
    },
    {} as Record<string, CategoryDetail>,
  );
  // only allow current categories and results where the filter text matches
  const getFilteredOptions = (category?: string, filter?: string) => {
    const opts = state?.options ?? [];

    if (!category && isExternalSearchUsed) return opts;

    // if no category is given, only search by filter
    const filterFn = category
      ? getFilterFunction({ category, filter, isExternalSearchUsed, groupSelectedInCategory })
      : (option: BaseOption<TValue>) => matchFilter(filter, option);

    return groupBySelectedOptions ? opts.filter(filterFn) : opts.filter(filterFn).sort(sortByIndex);
  };

  return {
    categoryDetail,
    clearAll,
    getOptions: getFilteredOptions,
    groupAndSortBySelectedOptions,
    optionsList: state?.options ?? [],
    selectAllInCategory,
    selectNoneInCategory,
    selectAll,
    selectNone,
    selectedOptions: convertMapToArray(state?.selectedOptions),
    showHeaders: Object.values(categoryDetail).length > 1 || showAllCategories,
    singleSelect,
    setSelectWithoutMatch,
    toggle,
  };
}
